/* eslint-disable react/no-unstable-nested-components */
import * as React from "react";
import { Badge, Button } from "@jhool-io/fe-components";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useFetchUpcomingSessions } from "../../upcoming-sessions/hooks/queries/upcoming-sessions.queries";
import {
    cn,
    formatDate,
    getClientDisplayName,
    getROILink,
    removeEnumUnderscore,
    showMoneyInAppFormat,
} from "../../../utils/helpers";
import ListState from "../../../components/ListState/ListState";
import Skeleton from "../../../components/Skeleton/Skeleton";
import VideoIcon from "../../../components/Icons/Video";
import {
    useFetchPaymentCards,
    useGetOutstandingBills,
} from "../../billing/hooks/billing.queries";
import useCurrentClientProfile from "../../../hooks/useCurrentClient";
import {
    useFetchUserDetails,
    useFetchUserPractices,
} from "../../auth/hooks/auth.queries";
import { IUpcomingSession } from "../../upcoming-sessions/types/upcoming-sessions.types";
import { FormCheckbox } from "../../../components/FormCheckbox/FormCheckbox";
import { FormStatus } from "../../client-forms/types/client-forms.types";
import {
    getStatusColors,
    getStatusDisplay,
} from "../../client-forms/helpers/client-forms.helpers";
import Navbar from "../../../components/Navbar/Navbar";
import SessionsActive from "./SessionsActive/SessionsActive";
import SessionsUpcoming from "./SessionsUpcoming/SessionsUpcoming";
import useTelehealthParticipants from "../../../hooks/useTelehealthParticipants";

export default function Dashboard() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    const { data, error, isLoading, isSuccess } = useFetchUpcomingSessions();

    const outstandingBillsData = useGetOutstandingBills({});
    const { data: cards } = useFetchPaymentCards();
    const { data: loggedInUser } = useFetchUserDetails();

    const sessionHasEnded = (session: IUpcomingSession) =>
        new Date(session.end_date_time).getTime() < Date.now();

    const nextUpcomingSession = data?.data?.find(
        (session) => !sessionHasEnded(session)
    );

    const isConsentDisabled =
        nextUpcomingSession?.consent?.status === FormStatus.RECEIVED ||
        nextUpcomingSession?.consent?.status === FormStatus.COMPLETED ||
        nextUpcomingSession?.consent?.status === FormStatus.PAUSED ||
        nextUpcomingSession?.consent?.status === FormStatus.CANCELLED;

    const displayName =
        loggedInUser?.data?.preferred_name ?? loggedInUser?.data?.first_name;

    const currentProfile = useCurrentClientProfile();
    const { data: userPractices } = useFetchUserPractices(
        currentProfile?.userId ?? ""
    );

    const selectedPractice = userPractices?.data?.practices.find(
        (practice) =>
            practice.practice_id === currentProfile?.practice.practice_id
    );

    const navigateToPayment = (id: string) => {
        searchParams.set("sessionHistoryId", id);
        if (cards?.data.length === 0) {
            searchParams.set("payment_method", "new_card");
        }
        navigate(`/payment-page?${searchParams.toString()}`);
    };

    const getJoinSessionPath = (session: IUpcomingSession) => {
        if (
            session.delivery_option !== "remote" ||
            !session.session_history_id ||
            sessionHasEnded(session)
        ) {
            return null;
        }
        const params = new URLSearchParams({
            with: `${session.provider.first_name}_${session.provider.last_name}`,
            service: session.service,
            start_date_time: session.start_date_time,
            display_name: displayName ?? "",
        });
        return `/meeting/${session.session_history_id}/waiting-room?${params.toString()}`;
    };

    const outstandingBalance =
        outstandingBillsData.data?.summary?.total_outstanding_amount;

    const paymentTextToShow = () => {
        if (!nextUpcomingSession) return "";
        if (nextUpcomingSession.payment_date) {
            return `Paid ${showMoneyInAppFormat(
                nextUpcomingSession.fee
            )} on ${formatDate(nextUpcomingSession.payment_date, true)}`;
        }
        return `Paid ${showMoneyInAppFormat(nextUpcomingSession.fee)}`;
    };

    const nextJoinPath = nextUpcomingSession
        ? getJoinSessionPath(nextUpcomingSession)
        : null;

    const { isProviderOnCall } = useTelehealthParticipants();

    const getPaymentAction = () => {
        if (!nextUpcomingSession) return null;

        const consentHandled =
            !nextUpcomingSession.consent ||
            !nextUpcomingSession.consent.consent_form_link ||
            nextUpcomingSession.consent.client_submitted ||
            isConsentDisabled;

        if (nextUpcomingSession.is_paid) {
            if (
                nextUpcomingSession.delivery_option === "remote" &&
                nextJoinPath &&
                consentHandled
            ) {
                return (
                    <div className="flex flex-col">
                        <Button
                            onClick={() => navigate(nextJoinPath)}
                            variant="normal"
                            size="small"
                            className={cn(
                                "shrink-0 gap-x-2 bg-primary text-white text-sm font-medium p-8 rounded-r8 border border-[#E1E4EA]"
                            )}
                        >
                            <VideoIcon stroke="#ffffff" />
                            Join session
                        </Button>
                        {isProviderOnCall(
                            nextUpcomingSession.session_history_id
                        ) && (
                            <div className="text-black text-xs italic flex items-center gap-x-[2px] mt-4">
                                <span className="size-8 rounded-full bg-primary blink" />
                                Provider has joined the call
                            </div>
                        )}
                    </div>
                );
            }
            return null;
        }

        return (
            <Button
                onClick={() =>
                    navigateToPayment(nextUpcomingSession.session_history_id)
                }
                className="shrink-0"
            >
                Pay {showMoneyInAppFormat(nextUpcomingSession.fee)}
            </Button>
        );
    };

    return (
        <div>
            <Navbar title="Dashboard" />
            {error && (
                <ListState
                    isError
                    stateHelperText="Please try again later"
                    errorMsg={
                        error.response?.data.message ||
                        "Error loading upcoming sessions"
                    }
                />
            )}

            {isLoading && (
                <div className="flex flex-col gap-y-32 bg-[linear-gradient(189deg,_#F4F9FD_18.83%,_#1A738D_346.17%)] px-16 md:px-32 pb-20 pt-24 md:pt-32">
                    <div className="flex flex-col gap-2">
                        <Skeleton width="30%" height={35} />
                        <Skeleton width="35%" height={30} />
                    </div>
                    <div className="grid gap-[10px] grid-cols-[repeat(auto-fit,minmax(250px,1fr))] min-[400px]:grid-cols-[repeat(auto-fit,minmax(300px,1fr))] min-[1370px]:grid-cols-[1fr,1fr]">
                        <Skeleton
                            containerTestId="next-session-loader"
                            width="100%"
                            height={200}
                        />
                        <Skeleton
                            containerTestId="outstanding-bill-loader"
                            width="100%"
                            height={200}
                        />
                    </div>
                    <Skeleton width="100%" height={200} />
                    <Skeleton width="100%" height={200} />
                </div>
            )}

            {isSuccess && data && (
                <div className="bg-[linear-gradient(189deg,_#F4F9FD_18.83%,_#1A738D_346.17%)] px-16 md:px-32 pb-20 pt-24 md:pt-32">
                    <div className="mb-16">
                        <p className="pb-2 text-xxl font-semibold">
                            Welcome back,{" "}
                            <span className="capitalize">
                                {getClientDisplayName(
                                    currentProfile?.preferredName,
                                    currentProfile?.firstName
                                )}
                            </span>
                        </p>
                        <p className="text-sm font-normal text-muted">
                            Here&apos;s everything to take care of before your
                            next session.
                        </p>
                    </div>

                    <div className="grid gap-[10px] grid-cols-[repeat(auto-fit,minmax(250px,1fr))] min-[400px]:grid-cols-[repeat(auto-fit,minmax(300px,1fr))] min-[1370px]:grid-cols-[1fr,1fr]">
                        <div className="rounded-r8 border border-white/70 bg-white/95 p-16 shadow-sm">
                            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-primary">
                                Get ready for your next session
                            </p>
                            {nextUpcomingSession ? (
                                <div>
                                    <p className="flex flex-wrap gap-1 text-sm">
                                        <span className="capitalize">
                                            {removeEnumUnderscore(
                                                nextUpcomingSession.service
                                            )}
                                        </span>
                                        on
                                        <span>
                                            {formatDate(
                                                nextUpcomingSession.start_date_time,
                                                false,
                                                "MM/dd/yyyy • h:mmaaa"
                                            )}
                                        </span>
                                    </p>
                                    {nextUpcomingSession.location
                                        ?.location_name &&
                                    nextUpcomingSession.delivery_option !==
                                        "remote" ? (
                                        <div className="flex items-center gap-2">
                                            <svg
                                                className="mt-[2px] h-12 w-12 shrink-0 text-primary"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                            >
                                                <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                                                <circle cx="12" cy="10" r="3" />
                                            </svg>
                                            <p className="text-xs text-black">
                                                {`${
                                                    nextUpcomingSession.location
                                                        ?.office_name
                                                }, ${
                                                    nextUpcomingSession.location
                                                        ?.location_name
                                                }`}
                                            </p>
                                        </div>
                                    ) : null}

                                    <div className="mt-6 space-y-4">
                                        {nextUpcomingSession.consent ? (
                                            <div className="flex items-center flex-wrap justify-between rounded-r8 bg-surface-subtle px-12 py-10">
                                                <div className="flex items-center gap-x-2">
                                                    <FormCheckbox
                                                        checked={
                                                            nextUpcomingSession
                                                                .consent
                                                                ?.client_submitted ||
                                                            isConsentDisabled
                                                        }
                                                        disabled
                                                        label="Consent form completed"
                                                        labelClass="!text-sm"
                                                        inputSize="large"
                                                    />
                                                    {nextUpcomingSession.consent
                                                        ?.status ? (
                                                        <Badge
                                                            content={getStatusDisplay(
                                                                nextUpcomingSession
                                                                    .consent
                                                                    .status
                                                            )}
                                                            bgColor={
                                                                getStatusColors(
                                                                    nextUpcomingSession
                                                                        .consent
                                                                        .status
                                                                ).bgColor
                                                            }
                                                            textColor={
                                                                getStatusColors(
                                                                    nextUpcomingSession
                                                                        .consent
                                                                        .status
                                                                ).textColor
                                                            }
                                                        />
                                                    ) : (
                                                        <Badge
                                                            content={getStatusDisplay(
                                                                FormStatus.SENT
                                                            )}
                                                            bgColor={
                                                                getStatusColors(
                                                                    FormStatus.SENT
                                                                ).bgColor
                                                            }
                                                            textColor={
                                                                getStatusColors(
                                                                    FormStatus.SENT
                                                                ).textColor
                                                            }
                                                        />
                                                    )}
                                                </div>
                                                {nextUpcomingSession.consent
                                                    ?.consent_form_link ? (
                                                    <a
                                                        href={
                                                            nextUpcomingSession
                                                                .consent
                                                                ?.consent_form_link
                                                        }
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="shrink-0 text-xs font-medium text-primary underline"
                                                    >
                                                        View consent form
                                                    </a>
                                                ) : null}
                                            </div>
                                        ) : null}

                                        <div className="rounded-r8 bg-surface-subtle px-12 py-10">
                                            <div className="flex items-center flex-wrap justify-between gap-8">
                                                <div>
                                                    <FormCheckbox
                                                        onChange={() => {}}
                                                        label={
                                                            nextUpcomingSession.is_paid
                                                                ? paymentTextToShow()
                                                                : "Session payment due"
                                                        }
                                                        labelClass="!text-sm"
                                                        checked={
                                                            nextUpcomingSession.is_paid
                                                        }
                                                        inputSize="large"
                                                    />
                                                </div>
                                                {getPaymentAction()}
                                            </div>
                                        </div>

                                        <div className="rounded-r8 border border-dashed border-[#D9E6EE] bg-white px-12 py-10">
                                            <div className="mt-8 flex items-center justify-between flex-wrap gap-8">
                                                <p className="text-sm text-black">
                                                    ROI (Release of Information)
                                                    form
                                                    <span className="text-muted text-xss">
                                                        (optional)
                                                    </span>
                                                </p>
                                                <a
                                                    href={getROILink(
                                                        selectedPractice?.practice_name ??
                                                            ""
                                                    )}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="shrink-0 text-xs font-medium text-primary underline"
                                                >
                                                    Fill out form
                                                </a>
                                            </div>
                                            <p className="text-muted text-xs">
                                                (Fill this out if you want
                                                another person to access your
                                                billing or appointment details,
                                                or view any forms you&apos;ve
                                                already submitted
                                                <Link
                                                    to="/forms"
                                                    className="ml-1 underline text-primary"
                                                >
                                                    here
                                                </Link>
                                                )
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <p className="mt-10 text-sm text-muted">
                                    Your checklist will appear here when your
                                    next session is scheduled.
                                </p>
                            )}
                        </div>

                        <div className="self-start rounded-r8 border border-white/70 bg-white/95 p-16 shadow-sm">
                            <p className="text-xs font-semibold uppercase tracking-[0.08em] text-primary">
                                Outstanding bills
                            </p>
                            <p className="text-xl font-semibold text-black">
                                {showMoneyInAppFormat(outstandingBalance ?? 0)}
                            </p>
                            <p className="mt-4 text-sm text-muted">
                                Current outstanding balance
                            </p>
                            <div className="mt-10">
                                <Link
                                    to="/billing"
                                    className="inline-flex items-center rounded-r8 border border-primary px-12 py-8 text-sm font-medium text-primary"
                                >
                                    View bills
                                </Link>
                            </div>
                        </div>
                    </div>
                    <SessionsUpcoming />
                    <SessionsActive />
                </div>
            )}
        </div>
    );
}
